/**
 * Wraps the native MediaRecorder with cross-browser quirk handling.
 *
 * - Safari: starts without timeslice (chunked recording corrupts audio).
 * - Produces a File with correct MIME type and extension.
 * - Guards against InvalidStateError when stopping an already-stopped recorder.
 * - Emits chunks via ondataavailable; assembles final blob in onstop.
 */

import { IS_SAFARI } from "@/lib/environmentDetector";
import { getBrowserSupportedMimeType, generateVoiceFilename } from "./mime";

export class AudioRecorder {
  /** The native MediaRecorder instance, null when not recording */
  private recorder: MediaRecorder | null = null;

  /** Accumulated audio data chunks from ondataavailable events */
  private chunks: Blob[] = [];

  /**
   * Resolve callback for the file promise returned by start().
   * Resolves with the recorded `File` on stop, or with `null` when the
   * recording is cancelled (cancellation is a normal outcome, not an error).
   */
  private resolve: ((file: File | null) => void) | null = null;

  /** Reject callback for the file promise returned by start() */
  private reject: ((error: Error) => void) | null = null;

  /** Whether a recording is currently active */
  get isRecording(): boolean {
    return this.recorder?.state === "recording";
  }

  /**
   * Start recording from the given microphone stream.
   *
   * Returns a Promise that resolves with the final audio File when `stop()` is
   * called, with `null` when `cancel()` is called (cancellation is normal, not
   * an error), or rejects only on a genuine recorder error.
   *
   * **Safari quirk**: Safari's MediaRecorder produces corrupted audio when
   * `start(timeslice)` is used, so we call `start()` without timeslice on Safari
   * and rely on a single `ondataavailable` at the end.
   *
   * @param stream - The MediaStream from MicrophoneManager.acquire().
   * @returns A Promise resolving to the recorded audio File, or null if cancelled.
   * @throws {Error} If already recording (synchronous rejection).
   */
  start(stream: MediaStream): Promise<File | null> {
    if (this.recorder?.state === "recording") {
      return Promise.reject(new Error("Already recording"));
    }

    this.chunks = [];

    const mimeType = getBrowserSupportedMimeType();
    const options = mimeType ? { mimeType } : {};
    const recorder = new MediaRecorder(stream, options);
    this.recorder = recorder;

    const filePromise = new Promise<File | null>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        this.chunks.push(e.data);
      }
    };

    recorder.onstop = () => {
      const mimeUsed = recorder.mimeType;
      const blob = new Blob(this.chunks, { type: mimeUsed });
      const filename = generateVoiceFilename(mimeUsed);

      const file = new File([blob], filename, {
        type: mimeUsed,
        lastModified: Date.now(),
      });

      this.chunks = [];
      const res = this.resolve;
      this.resolve = null;
      this.reject = null;
      this.recorder = null;
      res?.(file);
    };

    recorder.onerror = (e: Event) => {
      // MediaRecorder error events carry a DOMException on the .error property.
      // TypeScript's default lib doesn't include MediaRecorderErrorEvent, so we
      // use a loose cast to access it safely.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const domErr = (e as any)?.error as DOMException | undefined;
      const msg = "Recording error: " + (domErr?.message || "unknown error");
      this.chunks = [];
      const rej = this.reject;
      this.resolve = null;
      this.reject = null;
      this.recorder = null;
      rej?.(new Error(msg));
    };

    // Safari's MediaRecorder produces corrupted audio with timeslice,
    // so only use chunked recording on browsers that support it properly.
    if (IS_SAFARI) {
      recorder.start();
    } else {
      recorder.start(250);
    }

    return filePromise;
  }

  /**
   * Stops the recorder. The Promise from `start()` will resolve with the File.
   * Safe to call if not recording — no-ops silently.
   */
  stop(): void {
    if (this.recorder?.state === "recording") {
      this.recorder.stop();
    }
  }

  /**
   * Cancels the recording and discards all accumulated data.
   *
   * The Promise from `start()` **resolves with `null`** — cancellation is a
   * normal outcome, not an error, so callers never have to pre-catch a
   * rejection. This makes every cancel path (explicit cancel, ownership steal,
   * unmount cleanup) safe even when no handler is attached to the promise.
   *
   * The order of operations matters here:
   * 1. Capture resolve, then clear resolve/reject FIRST
   * 2. Stop the MediaRecorder (which fires onstop, but resolve is already null)
   * 3. Resolve the promise with null so any awaiter learns it was cancelled
   *
   * The `else` branch covers the stop()/cancel() race: `stop()` may have already
   * left the "recording" state while `onstop` is still pending, leaving the
   * promise parked and unsettled — we must settle it here too, or `await` hangs.
   */
  cancel(): void {
    // Capture resolve before clearing — onstop will find resolve=null and no-op
    const res = this.resolve;
    this.resolve = null;
    this.reject = null;

    if (this.recorder?.state === "recording") {
      this.recorder.stop();
    }
    this.recorder = null;
    this.chunks = [];

    // Settle a still-parked promise with null (no-op if already settled)
    res?.(null);
  }
}
